import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClockV2Icon } from '../../../assets/icons/clock-v2-icon.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PenaltyTransactionInterFace } from '../../../interface/penalty-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  fetchPenaltyTransaction,
  fetchPenaltyTransactionFilters,
  setSortDate,
  updatePenaltyTransactionFilters,
} from '../../../redux/slice/PenaltyTransactionSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import TransactionSidePanel from '../../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import penaltyManagementApi from '../../../utils/api/penalty-management';

/**
 * Getting duration in HH:MM:SS
 * @param start
 * @param end
 * @returns
 */
const getDurationHHMMSS = (start: string, end?: string) => {
  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : new Date().getTime();

  let diff = Math.max(0, endTime - startTime);

  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff %= 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));
  diff %= 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    seconds,
  ).padStart(2, '0')}`;
};

/**
 * Renders the Transactions table for Penalty Management
 */
function TransactionList() {
  const { t, i18n } = useTranslation('penaltyManagement');
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    penaltyTransaction,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
    isFilterUpdated,
  } = useAppSelector((state) => state.transactionPenalty);

  /**
   * Fetch Users list function
   */
  const fetchData = (data: any) => {
    if (!loading) {
      dispatch(fetchPenaltyTransaction({ ...data }));
    }
  };

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param currentRow
   * @returns
   */
  const renderDynamicPricing = (currentRow: PenaltyTransactionInterFace) => {
    if (!currentRow.dynamicPricingType || !currentRow.dynamicPricing) {
      return <p>{t('na')}</p>;
    }

    const type =
      currentRow.dynamicPricingType.charAt(0).toUpperCase() +
      currentRow.dynamicPricingType.slice(1);

    return (
      <div className="amount-in-transaction-table">
        <p className="table-row-each-data">
          {type} {currentRow.dynamicPricing}
        </p>
        <span>{branding.text.currency}</span>
      </div>
    );
  };

  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof PenaltyTransactionInterFace,
      sortAllow: false,
    },
    {
      label: t('transactionId'),
      accessorKey: 'chargingTransactionObjectId' as keyof PenaltyTransactionInterFace,
      sortAllow: false,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: 'chargingTransactionObjectId',
      sidePanelComponent: <TransactionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('transactionStartTime'),
      accessorKey: 'penaltyTransactionStartTime' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <p>
          {`${ConvertUTCtoDate(currentRow.penaltyTransactionStartTime)} - ${FormatUtcString(
            ConvertUTCtoHHMM(currentRow.penaltyTransactionStartTime),
          )}`}
        </p>
      ),
    },
    {
      label: t('transactionEndTime'),
      accessorKey: 'penaltyTransactionEndTime' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <p>
          {`${ConvertUTCtoDate(currentRow.penaltyTransactionEndTime)} - ${FormatUtcString(
            ConvertUTCtoHHMM(currentRow.penaltyTransactionEndTime),
          )}`}
        </p>
      ),
    },

    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       */
      customData: renderDynamicPricing,
    },
    {
      label: t('graceTime'),
      accessorKey: 'graceTime' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>
          {currentRow.graceTime}
          {t('mins')}
        </div>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'totalduration' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: PenaltyTransactionInterFace) => {
        const status = currentRow.status?.toLowerCase();

        if (status === 'inprogress') {
          const duration = currentRow.totalduration;

          return (
            <div className="tag-duration">
              <div>{duration}</div>
              <div>
                <ClockV2Icon />
              </div>
            </div>
          );
        }

        if (status === 'completed') {
          const duration = getDurationHHMMSS(
            currentRow.penaltyTransactionStartTime,
            currentRow.penaltyTransactionEndTime,
          );

          return <div>{duration}</div>;
        }

        return <div>---</div>;
      },
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof PenaltyTransactionInterFace,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: PenaltyTransactionInterFace) => {
        let output = <div />;
        switch (currentRow.status?.toLowerCase()) {
          case 'inprogress':
            output = <div className="tag-status progress">{t('inProgress')} </div>;
            break;
          case 'completed':
            output = <div className="tag-status completed">{t('completed')}</div>;
            break;
          default:
            break;
        }

        return output;
      },
    },
    {
      label: t('amount'),
      accessorKey: 'penaltyAmount' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {currentRow.penaltyAmount}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('transactionId'), accessorKey: 'chargingTransactionObjectId' },
    { label: t('transactionStartTime'), accessorKey: 'penaltyTransactionStartTime' },
    { label: t('transactionEndTime'), accessorKey: 'penaltyTransactionEndTime' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceTime'), accessorKey: 'graceTime' },
    { label: t('duration'), accessorKey: 'durationTime' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('amount'), accessorKey: 'penaltyAmount' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    penaltyManagementApi
      .transactionExport({ ...queryParameters })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: PenaltyTransactionInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'penaltyTransactionStartTime':
                return ConvertUTCtoHHMM(row.penaltyTransactionStartTime);
              case 'penaltyTransactionEndTime':
                return ConvertUTCtoHHMM(row.penaltyTransactionEndTime);
              case 'graceTime':
                return `${row.graceTime} ${t('mins')}`;
              case 'durationTime':
                return getDurationHHMMSS(
                  row.penaltyTransactionStartTime,
                  row.penaltyTransactionEndTime,
                );
              case 'penaltyAmount':
                return `${row.penaltyAmount}/-`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      page: changePage,
      limit,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortDate(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || penaltyTransaction) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
    }
  }, [filters]);

  return (
    <div className="penalty-transaction" dir={i18n.dir()}>
      <CustomTable
        header={column}
        data={penaltyTransaction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        currentPage={page}
        handleLimitChange={pageNateLimitChange} // ✅ REQUIRED
        sortData={{ sortBy, sortField }}
        handlePageChange={pageNateLimitChange}
        handleSortChange={handleSortFunction}
        filters={filters}
        tableHeight="60vh"
        filtersToDispatch={updatePenaltyTransactionFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchPenaltyTransactionFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
        exportData={penaltyTransaction}
        exportLoader={exportLoader}
        refreshCurrentPages={handleRefresh}
        sizeChangeButtonRequired
        leftTopContent={<div> </div>}
        rightTopContent={<div> </div>}
        topHeader={false}
        flexDirection
        paginate
      />
    </div>
  );
}
export default TransactionList;
